import type { PointOptionsObject } from "highcharts";

import type {
    MarketResearchDemandAnalysisCalculationModelsSearchTrendsNode,
    MarketResearchDemandAnalysisCommonModelsChangeGrowth,
    MarketResearchDemandAnalysisCalculationModelsNodeTrendPeriodData,
} from "@similarweb/pro-api-client";

import { translate } from "app/filters/i18nFilter";
import type { IPeriodOverPeriodCompareType } from "app/services/periodOverPeriodTypes";

import { TOTAL_KEYWORDS_ID } from "./utils/const";

declare module "highcharts" {
    interface PointOptionsObject {
        isMonthToDate?: boolean;
    }
}

interface IChartTooltipDataItemRow {
    id: string;
    label: {
        name: string;
        color?: string;
        date: string;
        isMonthToDate: boolean;
        isPreviousPeriod?: boolean;
    };
    volume: number;
    childrenCount?: number;
    monthlyChange?: {
        value: number;
        sentiment: MarketResearchDemandAnalysisCommonModelsChangeGrowth;
    };
    periodChange?: {
        value: number;
        sentiment: MarketResearchDemandAnalysisCommonModelsChangeGrowth;
    };
}

interface IChartTooltipDataItem {
    name?: string;
    date: string;
    rows: IChartTooltipDataItemRow[];
    isMonthToDate: boolean | null | undefined;
}

export type ChartTooltipData =
    | { isPeriodOverPeriod: false; data: IChartTooltipDataItem[] }
    | { isPeriodOverPeriod: true; data: Record<string, IChartTooltipDataItem[]> };

export interface IBaseLegendItem {
    id: string;
    name: string;
    value: number;
    children: MarketResearchDemandAnalysisCalculationModelsSearchTrendsNode[] | null | undefined;
}

export interface ILineChartSeriesData {
    id: string;
    name: string;
    isPeriodOverPeriod?: boolean;
    data: PointOptionsObject[];
    monthToDateIndex: number;
}

interface IDemandOverTimeData {
    lineChartData: ILineChartSeriesData[];
    categories: string[];
    chartTooltipData: ChartTooltipData;
    legendItems: IBaseLegendItem[];
    totalLegend: IBaseLegendItem | undefined;
}

export const initDemandOverTimeData: IDemandOverTimeData = {
    lineChartData: [],
    categories: [],
    chartTooltipData: { isPeriodOverPeriod: false, data: [] },
    legendItems: [],
    totalLegend: undefined,
};

const selectCategories = (data: MarketResearchDemandAnalysisCalculationModelsSearchTrendsNode): string[] => {
    return data.trend?.map((trendItem) => trendItem.currentPeriod.date) ?? [];
};

const selectLineChartData = (
    data: MarketResearchDemandAnalysisCalculationModelsSearchTrendsNode,
    periodOverPeriodType: IPeriodOverPeriodCompareType | null,
): ILineChartSeriesData[] => {
    if (!data.children) {
        return [];
    }

    const lineChartData: ILineChartSeriesData[] = [];

    data.children.forEach((child) => {
        const regularData: PointOptionsObject[] = [];
        const previousPeriodData: PointOptionsObject[] = [];
        let monthToDateIndex = -1;

        child.trend.forEach((trendItem, index) => {
            if (periodOverPeriodType != null && trendItem.previousPeriod) {
                previousPeriodData.push({
                    // id: child.name,
                    x: index,
                    y: trendItem.previousPeriod.volume,
                    custom: {
                        id: child.name,
                    },
                });
            }

            if (monthToDateIndex === -1 && trendItem.currentPeriod.isMonthToDate) {
                monthToDateIndex = index;
            }

            regularData.push({
                // id: child.name,
                x: index,
                y: trendItem.currentPeriod.volume,
                isMonthToDate: !!trendItem.currentPeriod.isMonthToDate,
                custom: {
                    id: child.name,
                },
            });
        });

        lineChartData.push({
            id: child.name,
            name: child.name,
            data: regularData,
            monthToDateIndex,
        });

        if (periodOverPeriodType != null && previousPeriodData.length > 0) {
            lineChartData.push({
                id: child.name,
                name: child.name,
                isPeriodOverPeriod: true,
                data: previousPeriodData,
                monthToDateIndex: -1,
            });
        }
    });

    lineChartData.push({
        id: TOTAL_KEYWORDS_ID,
        name: translate("common.total"),
        data:
            data.trend?.map((trendItem, index) => ({
                x: index,
                y: trendItem.currentPeriod.volume,
                custom: {
                    id: TOTAL_KEYWORDS_ID,
                },
            })) ?? [],
        monthToDateIndex: data.trend?.findIndex((trendItem) => trendItem.currentPeriod.isMonthToDate),
    });

    if (periodOverPeriodType != null) {
        lineChartData.push({
            id: TOTAL_KEYWORDS_ID,
            name: translate("common.total"),
            isPeriodOverPeriod: true,
            data:
                data.trend?.map((trendItem, index) => ({
                    x: index,
                    y: trendItem.previousPeriod?.volume ?? 0,
                    custom: {
                        id: TOTAL_KEYWORDS_ID,
                    },
                })) ?? [],
            monthToDateIndex: -1,
        });
    }

    return lineChartData;
};

const createChartTooltipDataRow = ({
    id,
    name,
    data,
    childrenCount,
    isPeriodOverPeriod,
}: {
    id: string;
    name?: string;
    data: MarketResearchDemandAnalysisCalculationModelsNodeTrendPeriodData;
    childrenCount?: number;
    isPeriodOverPeriod?: boolean;
}): IChartTooltipDataItemRow => {
    return {
        id,
        label: {
            name: name ?? id,
            date: data.date,
            isMonthToDate: Boolean(data.isMonthToDate),
            isPreviousPeriod: Boolean(isPeriodOverPeriod),
        },
        childrenCount,
        volume: data.volume,
        monthlyChange: data.monthlyVolumeChange
            ? { value: data.monthlyVolumeChange.change, sentiment: data.monthlyVolumeChange.growth }
            : undefined,
        periodChange: data.periodVolumeChange
            ? { value: data.periodVolumeChange.change, sentiment: data.periodVolumeChange.growth }
            : undefined,
    };
};

const selectChartTooltipData = (
    data: MarketResearchDemandAnalysisCalculationModelsSearchTrendsNode,
): IChartTooltipDataItem[] => {
    const { trend, children } = data;

    if (trend && children) {
        return trend.map((trendItem, index) => {
            const rows =
                children
                    .map((child) => {
                        const childTrend = child.trend?.[index];

                        if (!childTrend) {
                            return null;
                        }

                        return createChartTooltipDataRow({
                            id: child.name,
                            name: child.children?.[0]?.name ?? child.name,
                            data: childTrend.currentPeriod,
                            childrenCount: child.children?.length,
                        });
                    })
                    .filter((item) => item !== null) ?? [];
            const totalChildrenCount = rows.reduce((subtotal, { childrenCount = 0 }) => subtotal + childrenCount, 0);

            rows.push(
                createChartTooltipDataRow({
                    id: TOTAL_KEYWORDS_ID,
                    name: translate("common.total"),
                    data: trendItem.currentPeriod,
                    childrenCount: totalChildrenCount,
                }),
            );

            return {
                date: trendItem.currentPeriod.date,
                rows,
                isMonthToDate: Boolean(trendItem.currentPeriod.isMonthToDate),
            };
        });
    }

    return [];
};

const selectChartTooltipDataPreviousPeriod = (
    data: MarketResearchDemandAnalysisCalculationModelsSearchTrendsNode,
): Record<string, IChartTooltipDataItem[]> => {
    const { trend, children } = data;

    if (!trend || !children) {
        return {};
    }

    const result: Record<string, IChartTooltipDataItem[]> = Object.fromEntries(
        children.map((child) => [
            child.name,
            trend.map((trendItem, index) => {
                const name = child.children?.[0]?.name ?? child.name;
                const childTrend = child.trend?.[index];
                const rows: IChartTooltipDataItemRow[] = [];

                if (childTrend) {
                    rows.push(
                        createChartTooltipDataRow({
                            id: child.name,
                            name,
                            data: childTrend.currentPeriod,
                            isPeriodOverPeriod: false,
                        }),
                    );

                    if (childTrend.previousPeriod) {
                        rows.unshift(
                            createChartTooltipDataRow({
                                id: child.name,
                                name,
                                data: childTrend.previousPeriod,
                                isPeriodOverPeriod: true,
                            }),
                        );
                    }
                }

                return {
                    name,
                    rows,
                    date: trendItem.currentPeriod.date,
                    isMonthToDate: Boolean(trendItem.currentPeriod.isMonthToDate),
                };
            }),
        ]),
    );

    result[TOTAL_KEYWORDS_ID] = trend.map((trendItem) => {
        const rows: IChartTooltipDataItemRow[] = [
            createChartTooltipDataRow({
                id: TOTAL_KEYWORDS_ID,
                name: translate("common.total"),
                data: trendItem.currentPeriod,
                isPeriodOverPeriod: false,
            }),
        ];

        if (trendItem.previousPeriod) {
            rows.unshift(
                createChartTooltipDataRow({
                    id: TOTAL_KEYWORDS_ID,
                    name: translate("common.total"),
                    data: trendItem.previousPeriod,
                    isPeriodOverPeriod: true,
                }),
            );
        }

        return {
            name: translate("common.total"),
            date: trendItem.currentPeriod.date,
            rows,
            isMonthToDate: Boolean(trendItem.currentPeriod.isMonthToDate),
        };
    });

    return result;
};

const selectLegendItems = (data: MarketResearchDemandAnalysisCalculationModelsSearchTrendsNode): IBaseLegendItem[] => {
    if (!data.children) {
        return [];
    }

    return data.children.map((child) => {
        let name = child.name;

        if (child.type === "Cluster" && child.children?.[0]?.name) {
            name = child.children?.[0]?.name;
        }

        return {
            id: child.name,
            name,
            value: child.currentPeriod.averageVolume,
            children: child.children,
        };
    });
};

const selectTotalLegend = (
    data: MarketResearchDemandAnalysisCalculationModelsSearchTrendsNode,
): IBaseLegendItem | undefined => {
    if (!data.currentPeriod?.volume) {
        return undefined;
    }

    return {
        id: TOTAL_KEYWORDS_ID,
        name: translate("common.total"),
        value: data.currentPeriod.averageVolume,
        children: null,
    };
};

export const selectDemandOverTimeData = (
    data: MarketResearchDemandAnalysisCalculationModelsSearchTrendsNode,
    periodOverPeriodType: IPeriodOverPeriodCompareType | null,
): IDemandOverTimeData => {
    if (!data.children || !data.trend) {
        return initDemandOverTimeData;
    }

    return {
        lineChartData: selectLineChartData(data, periodOverPeriodType),
        categories: selectCategories(data),
        chartTooltipData:
            periodOverPeriodType != null
                ? { isPeriodOverPeriod: true, data: selectChartTooltipDataPreviousPeriod(data) }
                : { isPeriodOverPeriod: false, data: selectChartTooltipData(data) },
        legendItems: selectLegendItems(data),
        totalLegend: selectTotalLegend(data),
    };
};
